import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '../../API/Main/API';
import { ErrorProperties } from '../../API/identityAPIProperties';
import {
	GetScheduleAllotmentReportProperties,
	GetScheduleReportProperties,
	ScheduleAllotmentReportRowsProperties,
	ScheduleCancellationReportRowsProperties,
	ScheduleReportCacheStatusProperties,
	ScheduleReportRowsProperties,
	ScheduleUtilizationReportRowsProperties,
} from '../../API/ClientAPIHelpers/scheduleReportProperties';

interface ScheduleReportInitialStateProperties {
	allotmentRows: ScheduleAllotmentReportRowsProperties;
	cacheStatus: ScheduleReportCacheStatusProperties | null;
	cancellationRows: ScheduleCancellationReportRowsProperties;
	error: ErrorProperties;
	loading: boolean;
	rows: ScheduleReportRowsProperties;
	utilizationRows: ScheduleUtilizationReportRowsProperties;
}

const emptyRows = {
	query: null,
};

const initialState: ScheduleReportInitialStateProperties = {
	loading: false,
	cacheStatus: null,
	rows: emptyRows,
	cancellationRows: emptyRows,
	utilizationRows: emptyRows,
	allotmentRows: emptyRows,
	error: {
		status: 0,
		title: '',
	},
};

export const getScheduleReportRows = createAsyncThunk(
	'scheduleReport/rows',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.getRows(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleReportRowsProperties;
	},
);

export const getCancellationReportRows = createAsyncThunk(
	'scheduleReport/cancellationRows',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.getCancellationRows(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleCancellationReportRowsProperties;
	},
);

export const getScheduleUtilizationReportRows = createAsyncThunk(
	'scheduleReport/utilizationRows',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.getUtilizationRows(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleUtilizationReportRowsProperties;
	},
);

export const getScheduleAllotmentReportRows = createAsyncThunk(
	'scheduleReport/allotmentRows',
	async (data: GetScheduleAllotmentReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.getAllotmentRows(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleAllotmentReportRowsProperties;
	},
);

export const getScheduleReportCacheStatus = createAsyncThunk(
	'scheduleReport/cacheStatus',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.getCacheStatus(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleReportCacheStatusProperties;
	},
);

export const refreshScheduleReportCache = createAsyncThunk(
	'scheduleReport/refreshCache',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.refreshCache(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as ScheduleReportCacheStatusProperties;
	},
);

export const downloadScheduleReportCsv = createAsyncThunk(
	'scheduleReport/download/csv',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.downloadXlsx(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

export const downloadCancellationReportCsv = createAsyncThunk(
	'scheduleReport/download/cancellationCsv',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.downloadCancellationXlsx(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

export const downloadScheduleUtilizationReportCsv = createAsyncThunk(
	'scheduleReport/download/utilizationCsv',
	async (data: GetScheduleReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.downloadUtilizationXlsx(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

export const downloadScheduleAllotmentReportCsv = createAsyncThunk(
	'scheduleReport/download/allotmentCsv',
	async (data: GetScheduleAllotmentReportProperties, thunkAPI) => {
		const response = await API.ClientAPI.ScheduleReport.downloadAllotmentXlsx(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as {
			file: Blob;
			fileName: string;
		};
	},
);

const ScheduleReportSlice = createSlice({
	name: 'scheduleReport',
	initialState,
	reducers: {
		resetScheduleReportRows: (state) => {
			state.rows = emptyRows;
			state.cancellationRows = emptyRows;
			state.utilizationRows = emptyRows;
			state.allotmentRows = emptyRows;
			state.loading = true;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getScheduleReportRows.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getScheduleReportRows.fulfilled, (state, action) => {
			state.loading = false;
			state.rows = action.payload;
			state.error = initialState.error;
		});
		builder.addCase(getScheduleReportRows.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		builder.addCase(getCancellationReportRows.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getCancellationReportRows.fulfilled, (state, action) => {
			state.loading = false;
			state.cancellationRows = action.payload;
			state.error = initialState.error;
		});
		builder.addCase(getCancellationReportRows.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		builder.addCase(getScheduleUtilizationReportRows.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getScheduleUtilizationReportRows.fulfilled, (state, action) => {
			state.loading = false;
			state.utilizationRows = action.payload;
			state.error = initialState.error;
		});
		builder.addCase(getScheduleUtilizationReportRows.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		builder.addCase(getScheduleAllotmentReportRows.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getScheduleAllotmentReportRows.fulfilled, (state, action) => {
			state.loading = false;
			state.allotmentRows = action.payload;
			state.error = initialState.error;
		});
		builder.addCase(getScheduleAllotmentReportRows.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		builder.addCase(getScheduleReportCacheStatus.fulfilled, (state, action) => {
			state.cacheStatus = action.payload;
			state.error = initialState.error;
		});
		builder.addCase(getScheduleReportCacheStatus.rejected, (state, action) => {
			state.error = action.payload as ErrorProperties;
		});
		builder.addCase(refreshScheduleReportCache.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(refreshScheduleReportCache.fulfilled, (state, action) => {
			state.loading = false;
			state.cacheStatus = action.payload;
			state.error = initialState.error;
		});
		builder.addCase(refreshScheduleReportCache.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});
		[
			downloadScheduleReportCsv,
			downloadCancellationReportCsv,
			downloadScheduleUtilizationReportCsv,
			downloadScheduleAllotmentReportCsv,
		].forEach((downloadThunk) => {
			builder.addCase(downloadThunk.pending, (state) => {
				state.loading = true;
			});
			builder.addCase(downloadThunk.fulfilled, (state) => {
				state.loading = false;
				state.error = initialState.error;
			});
			builder.addCase(downloadThunk.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as ErrorProperties;
			});
		});
	},
});

export const { resetScheduleReportRows } = ScheduleReportSlice.actions;
export default ScheduleReportSlice.reducer;
